/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : LinkHomePage.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Grid } from "@mui/material";
import React, { useState } from "react";
import CustomTabsV2 from "../../common/tabs/CustomTabsV2";
import EELinkPage from "./EELinkPage";
import SWMLinkPage from "./SWMLinkPage";
import ProjectVariantSearchbar from "../../common/projectSearchbar/ProjectvariantSearchbar";
import ProjectVariantDialog from "../ProjectVarientAccordian/ProjectVariantDialog";
import CommonBreadcrumbs from "../../common/Breadcrumbs/CommonBreadcrumbs";
import { useLocation } from "react-router-dom";
import CustomButtonV2 from "../../common/button/CustomButtonV2";
import { HandleApiActions } from "../../../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import smartMatchActions from "../../../../redux/actions/smart-matching/smartMatchSuggestionActions";
import { toast } from "react-toastify";

function LinkHomePage() {
  const [filterData, setFilterData] = useState({
    sortBy: "created_at",
    sortOrder: "ASC",
    component: "EE",
    componentTypeAll: true,
    componentTypeDraft: true,
    componentTypeActionRequired: true,
  });

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  // Support both `tab` and legacy `tabFromurl` for default tab selection
  const tabParamRaw = queryParams.get("tab") ?? queryParams.get("tabFromurl");
  const tabFromurl = Number(tabParamRaw);
  const eeFromurl = queryParams.get("ee_id");
  const swmFromurl = queryParams.get("swm_id");

  const dispatch = useDispatch();
  const userProfile = useSelector((state) => state.profileReducer.profile);

  const [anchorVlcFilter, setAnchorVlcFilter] = useState(null);
  const [openVlcFilter, setOpenVlcFilter] = useState(false);

  /* Smart matching states  */
  const [kgLoading, setKgLoading] = useState(false);

  const handleKgBulkRefresh = async () => {
    if (kgLoading) return;

    setKgLoading(true);

    try {
      let resp = await dispatch(
        HandleApiActions({
          https_method: "POST",
          ...smartMatchActions?.refresh_kg_nodes,
          params: {},
          show_toast: false,
          ignore_app_loading_spinner: true,

        }),
      );
      toast.success(resp?.message);

      if (resp?.jobId) {
        const pollJobStatus = async () => {
          const streamResp = await dispatch(
            HandleApiActions({
              https_method: "GET",
              ...smartMatchActions?.get_live_stream_queue,
              api_name: `/smartMatch/api/v1/kg/queue/stream/${resp.jobId}`,
              params: {},
              show_toast: false,
              ignore_app_loading_spinner: true,

            }),
          );



          let state = null;

          if (typeof streamResp === "string") {
            const lines = streamResp.split("\n").filter((line) => line.trim());
            const lastLine = lines[lines.length - 1];

            if (lastLine.startsWith("data:")) {
              try {
                const jsonStr = lastLine.replace(/^data:/, "").trim();
                const parsed = JSON.parse(jsonStr);
                state = parsed.state;
              } catch (e) {
              }
            }
          } else {
            state = streamResp?.state || streamResp?.data?.state;
          }

          if (state === "COMPLETED") {
            toast.success("Knowledge Graph Build Completed.");
          } else if (state === "RUNNING" || state === "QUEUED") {
            setTimeout(pollJobStatus, 2000);
          }
        };

        pollJobStatus();
      }
    } finally {
      setKgLoading(false);
    }
  };

  const handleHsFilterIconClose = () => {
    setAnchorVlcFilter(null);
    setOpenVlcFilter(false);
  };

  const handleFilterCheckboxChange = (name) => {
    let updated = { ...filterData, [name]: !filterData[name] };

    if (name === "componentTypeAll") {
      const newValue = !filterData.componentTypeAll;
      updated = {
        componentTypeAll: newValue,
        componentTypeDraft: newValue,
        componentTypeActionRequired: newValue,
        componentTypeGithub: newValue,
      };
    } else {
      const {
        componentTypeDraft,
        componentTypeActionRequired,
        componentTypeGithub,
      } = updated;
      updated.componentTypeAll = !!(
        componentTypeDraft &&
        componentTypeActionRequired &&
        componentTypeGithub
      );
    }

    setFilterData({
      ...filterData,
      ...updated,
    });
  };

  const handleFliterClick = () => {
    // setGetApiDataforFilter(!getApiDataforFilter);
  };

  const handleVlcFilterButton = (action) => {
    switch (action) {
      case "cancel":
        handleHsFilterIconClose();
        break; // prevents fall-through
      case "apply":
        handleFliterClick();
        handleHsFilterIconClose();
        break;
      default:
    }
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        {/* 🔹 Breadcrumb area */}
        <Box sx={{ p: 1 }}>
          <CommonBreadcrumbs
            items={[
              { label: "Virtual Lab-Car" },
              { label: "Linked Components" },
            ]}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            ml: 1,
          }}
        >

          <Box>
            <ProjectVariantSearchbar />
          </Box>
          <ProjectVariantDialog />
          {/* Adding button as a fallback for creating edges, nodes and embeddings as needed */}
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "flex-end",
              mr: 2,
            }}
          >
            <CustomButtonV2
              type="other"
              size="small"
              btnName={kgLoading ? "Refreshing…" : "Refresh AI Suggestions"}
              handleOnClick={handleKgBulkRefresh}
              disabled={kgLoading}
            />
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <CustomTabsV2
          defaultTab={!Number.isNaN(tabFromurl) ? tabFromurl : 0}
          tabs={[
            {
              name: "EE Component",
              count: "",
              content: <EELinkPage onBeforeSmartMatch={handleKgBulkRefresh} />,
            },
            {
              name: "Software Module",
              count: "",
              content: <SWMLinkPage onBeforeSmartMatch={handleKgBulkRefresh} />,
            },
          ]}
          centerTabs={true}
        />
      </Grid>
    </Grid>
  );
}

export default LinkHomePage;
